import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Plus, Pencil, Trash2, Eye, EyeOff, Home, FileText, ShoppingBag, Mail, ChevronUp, ChevronDown, Settings, Menu, GripVertical, Type, Image, Columns, MessageSquare, HelpCircle, Video, Star, Users, Megaphone, Code, ExternalLink, UploadCloud, Server, CheckCircle, XCircle, Loader2, Sparkles, Send, MapPin } from 'lucide-react';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useToast } from '../../../components/Toast';
import Modal from '../../../components/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import api from '../../../services/api';

// Build a usable URL for an uploaded media path
const mediaUrl = (v) => !v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/')) ? v : '/uploads/' + v;

// ==================== BLOCK TYPE DEFS ====================

const BLOCK_TYPES = [
  { type: 'hero', label: 'Baner Hero', icon: Star, color: 'purple', defaults: { title: '', subtitle: '', text: '', cta_text: 'Dowiedz sie wiecej', cta_url: '#', background_color: '#1e40af' } },
  { type: 'text', label: 'Tekst', icon: Type, color: 'gray', defaults: { html: '<p>Tresc...</p>' } },
  { type: 'heading', label: 'Naglowek', icon: Type, color: 'blue', defaults: { text: 'Naglowek', level: 2, align: 'left' } },
  { type: 'image', label: 'Zdjecie', icon: Image, color: 'green', defaults: { src: '', alt: '', caption: '' } },
  { type: 'features', label: 'Cechy / Kafelki', icon: Columns, color: 'indigo', defaults: { items: [{ icon: 'Star', title: 'Cecha 1', description: 'Opis cechy' }], columns: 3 } },
  { type: 'cta', label: 'Call to Action', icon: Megaphone, color: 'red', defaults: { title: '', text: '', button_text: 'Kliknij', button_url: '#', style: 'primary' } },
  { type: 'faq', label: 'FAQ', icon: HelpCircle, color: 'amber', defaults: { items: [{ question: 'Pytanie?', answer: 'Odpowiedz.' }] } },
  { type: 'contact_form', label: 'Formularz', icon: Mail, color: 'teal', defaults: { form_id: null, title: 'Kontakt', description: '' } },
  { type: 'testimonials', label: 'Opinie', icon: MessageSquare, color: 'pink', defaults: { items: [{ name: '', company: '', text: '' }] } },
  { type: 'video', label: 'Video', icon: Video, color: 'red', defaults: { url: '', title: '' } },
  { type: 'html', label: 'HTML', icon: Code, color: 'gray', defaults: { code: '' } },
  { type: 'team', label: 'Zespol', icon: Users, color: 'blue', defaults: { members: [{ name: '', role: '', bio: '' }] } },
  { type: 'gallery_category', label: 'Galeria (kategoria)', icon: Image, color: 'green', defaults: { category_id: null, columns: 3, title: '' } },
  { type: 'latest_realizations', label: 'Aktualności / realizacje', icon: Megaphone, color: 'amber', defaults: { category_id: null, count: 4, title: 'Nasze realizacje' } },
  { type: 'realizations_map', label: 'Mapa realizacji', icon: MapPin, color: 'teal', defaults: { category_id: null, title: 'Mapa realizacji', height: 420 } },
];

const getBlockDef = (type) => BLOCK_TYPES.find(b => b.type === type) || { label: type, icon: FileText, color: 'gray' };

// ==================== BLOCK EDITOR COMPONENT ====================

function BlockEditor({ block, onSave, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(block.content || {});
  const [uploading, setUploading] = useState(false);
  const [galleryCats, setGalleryCats] = useState([]);
  useEffect(() => {
    if (['gallery_category', 'latest_realizations', 'realizations_map'].includes(block.block_type)) {
      api.get('/cms/gallery/categories').then(r => setGalleryCats(r.data.categories || [])).catch(() => {});
    }
  }, [block.block_type]);
  const def = getBlockDef(block.block_type);
  const Icon = def.icon;

  const handleSave = () => {
    onSave(block.id, content);
    setEditing(false);
  };

  // Upload an image and store src + WebP/srcset variants + dimensions (§6)
  const uploadImageInto = async (file, apply) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/cms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      apply({ src: r.data.filename, webp: r.data.webp, variants: r.data.variants, width: r.data.width, height: r.data.height });
    } catch (e) { /* surfaced by caller */ }
    finally { setUploading(false); }
  };

  const renderEditFields = () => {
    switch (block.block_type) {
      case 'hero':
        return (<div className="space-y-2">
          <input type="text" value={content.title||''} onChange={e => setContent({...content, title: e.target.value})} placeholder="Tytul" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <input type="text" value={content.subtitle||''} onChange={e => setContent({...content, subtitle: e.target.value})} placeholder="Podtytul" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <textarea value={content.text||''} onChange={e => setContent({...content, text: e.target.value})} placeholder="Tekst" className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={content.cta_text||''} onChange={e => setContent({...content, cta_text: e.target.value})} placeholder="Tekst przycisku" className="w-full px-3 py-2 border rounded-lg text-sm" />
            <input type="text" value={content.cta_url||''} onChange={e => setContent({...content, cta_url: e.target.value})} placeholder="URL przycisku" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <input type="color" value={content.background_color||'#1e40af'} onChange={e => setContent({...content, background_color: e.target.value})} className="h-10 w-20 rounded border" />
        </div>);
      case 'text':
        return <textarea value={content.html||''} onChange={e => setContent({...content, html: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" rows={8} placeholder="<p>Tresc HTML...</p>" />;
      case 'heading':
        return (<div className="space-y-2">
          <input type="text" value={content.text||''} onChange={e => setContent({...content, text: e.target.value})} placeholder="Tekst naglowka" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <div className="flex gap-2">
            <select value={content.level||2} onChange={e => setContent({...content, level: parseInt(e.target.value)})} className="px-3 py-2 border rounded-lg text-sm">
              {[1,2,3,4,5,6].map(l => <option key={l} value={l}>H{l}</option>)}
            </select>
            <select value={content.align||'left'} onChange={e => setContent({...content, align: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
              <option value="left">Lewo</option><option value="center">Srodek</option><option value="right">Prawo</option>
            </select>
          </div>
        </div>);
      case 'cta':
        return (<div className="space-y-2">
          <input type="text" value={content.title||''} onChange={e => setContent({...content, title: e.target.value})} placeholder="Tytul" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <textarea value={content.text||''} onChange={e => setContent({...content, text: e.target.value})} placeholder="Tekst" className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={content.button_text||''} onChange={e => setContent({...content, button_text: e.target.value})} placeholder="Tekst przycisku" className="w-full px-3 py-2 border rounded-lg text-sm" />
            <input type="text" value={content.button_url||''} onChange={e => setContent({...content, button_url: e.target.value})} placeholder="URL" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>);
      case 'features':
        return (<div className="space-y-2">
          {(content.items||[]).map((item, i) => (
            <div key={i} className="border rounded p-2 space-y-1">
              <input type="text" value={item.title||''} onChange={e => { const items=[...(content.items||[])]; items[i]={...items[i], title: e.target.value}; setContent({...content, items}); }} placeholder="Tytul" className="w-full px-2 py-1 border rounded text-sm" />
              <textarea value={item.description||''} onChange={e => { const items=[...(content.items||[])]; items[i]={...items[i], description: e.target.value}; setContent({...content, items}); }} placeholder="Opis" className="w-full px-2 py-1 border rounded text-sm" rows={2} />
            </div>
          ))}
          <button onClick={() => setContent({...content, items: [...(content.items||[]), { icon:'Star', title:'', description:'' }]})} className="text-xs text-blue-600 hover:underline">+ Dodaj ceche</button>
        </div>);
      case 'faq':
        return (<div className="space-y-2">
          {(content.items||[]).map((item, i) => (
            <div key={i} className="border rounded p-2 space-y-1">
              <input type="text" value={item.question||''} onChange={e => { const items=[...(content.items||[])]; items[i]={...items[i], question: e.target.value}; setContent({...content, items}); }} placeholder="Pytanie" className="w-full px-2 py-1 border rounded text-sm" />
              <textarea value={item.answer||''} onChange={e => { const items=[...(content.items||[])]; items[i]={...items[i], answer: e.target.value}; setContent({...content, items}); }} placeholder="Odpowiedz" className="w-full px-2 py-1 border rounded text-sm" rows={2} />
            </div>
          ))}
          <button onClick={() => setContent({...content, items: [...(content.items||[]), { question:'', answer:'' }]})} className="text-xs text-blue-600 hover:underline">+ Dodaj pytanie</button>
        </div>);
      case 'image':
        return (<div className="space-y-2">
          <div className="flex items-center gap-3">
            {content.src
              ? <img src={mediaUrl(content.src)} alt="" className="h-16 max-w-[140px] object-contain border rounded bg-gray-50 p-1" />
              : <div className="h-16 w-24 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-300 text-xs">brak</div>}
            <div>
              <input type="file" accept="image/*" onChange={e => uploadImageInto(e.target.files[0], (img) => setContent({ ...content, ...img }))} className="text-xs" />
              {uploading && <span className="text-xs text-gray-400 ml-2">Wgrywanie…</span>}
              {content.width && <div className="text-xs text-gray-400 mt-1">{content.width}×{content.height}px {content.variants ? `· ${content.variants.length} wariantów WebP` : ''}</div>}
            </div>
          </div>
          <input type="text" value={content.src || ''} onChange={e => setContent({ ...content, src: e.target.value, webp: undefined, variants: undefined })} placeholder="albo wklej URL obrazu" className="w-full px-3 py-2 border rounded-lg text-sm font-mono" />
          <input type="text" value={content.alt || ''} onChange={e => setContent({ ...content, alt: e.target.value })} placeholder="Tekst alternatywny (alt) — wymagany dla SEO/dostępności" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <input type="text" value={content.caption || ''} onChange={e => setContent({ ...content, caption: e.target.value })} placeholder="Podpis (opcjonalnie)" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>);
      case 'gallery_category':
        return (<div className="space-y-2">
          <select value={content.category_id || ''} onChange={e => setContent({ ...content, category_id: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="">— wybierz kategorię galerii —</option>
            {galleryCats.map(c => <option key={c.id} value={c.id}>{c.name} ({c.items_count})</option>)}
          </select>
          <input type="text" value={content.title || ''} onChange={e => setContent({ ...content, title: e.target.value })} placeholder="Tytuł sekcji (opcjonalnie)" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <label className="text-sm flex items-center gap-2">Kolumny: <select value={content.columns || 3} onChange={e => setContent({ ...content, columns: Number(e.target.value) })} className="px-2 py-1 border rounded">{[2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}</select></label>
        </div>);
      case 'latest_realizations':
        return (<div className="space-y-2">
          <select value={content.category_id || ''} onChange={e => setContent({ ...content, category_id: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="">— wybierz kategorię realizacji —</option>
            {galleryCats.map(c => <option key={c.id} value={c.id}>{c.name} ({c.items_count})</option>)}
          </select>
          <input type="text" value={content.title || ''} onChange={e => setContent({ ...content, title: e.target.value })} placeholder="Tytuł (np. Aktualności)" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <label className="text-sm flex items-center gap-2">Ile pokazać: <input type="number" min="1" max="12" value={content.count || 4} onChange={e => setContent({ ...content, count: Number(e.target.value) })} className="w-20 px-2 py-1 border rounded" /></label>
          <p className="text-xs text-gray-400">Losowo wybiera spośród najnowszych realizacji z kategorii (odświeża się przy każdej publikacji).</p>
        </div>);
      case 'realizations_map':
        return (<div className="space-y-2">
          <select value={content.category_id || ''} onChange={e => setContent({ ...content, category_id: e.target.value ? Number(e.target.value) : null })} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="">— wszystkie kategorie —</option>
            {galleryCats.map(c => <option key={c.id} value={c.id}>{c.name} ({c.items_count})</option>)}
          </select>
          <input type="text" value={content.title || ''} onChange={e => setContent({ ...content, title: e.target.value })} placeholder="Tytuł (np. Mapa realizacji)" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <label className="text-sm flex items-center gap-2">Wysokość (px): <input type="number" min="200" max="800" value={content.height || 420} onChange={e => setContent({ ...content, height: Number(e.target.value) })} className="w-24 px-2 py-1 border rounded" /></label>
          <p className="text-xs text-gray-400">Pinezki pochodzą z wpisów galerii, które mają uzupełnione współrzędne (szer./dł.). Mapa: Leaflet + OpenStreetMap, bez klucza API.</p>
        </div>);
      default:
        return <textarea value={JSON.stringify(content, null, 2)} onChange={e => { try { setContent(JSON.parse(e.target.value)); } catch {} }} className="w-full px-3 py-2 border rounded-lg text-xs font-mono" rows={6} />;
    }
  };

  const renderPreview = () => {
    switch (block.block_type) {
      case 'hero': return <div><strong>{content.title}</strong> <span className="text-gray-500">{content.subtitle}</span></div>;
      case 'text': return <div className="text-xs text-gray-500 truncate" dangerouslySetInnerHTML={{ __html: (content.html||'').substring(0, 100) }} />;
      case 'heading': return <div className="font-bold">{content.text}</div>;
      case 'cta': return <div>{content.title} <span className="text-blue-600">[{content.button_text}]</span></div>;
      case 'features': return <div>{(content.items||[]).length} cech/kafelkow</div>;
      case 'faq': return <div>{(content.items||[]).length} pytan</div>;
      default: return <div className="text-xs text-gray-400">{block.block_type}</div>;
    }
  };

  return (
    <div className={`border rounded-lg overflow-hidden ${editing ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-white'} group`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <GripVertical size={14} className="text-gray-300 cursor-grab" />
        <Icon size={14} className={`text-${def.color}-500`} />
        <span className="text-xs font-medium text-gray-600 w-24">{def.label}</span>
        <div className="flex-1 text-sm truncate">{renderPreview()}</div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
          {!isFirst && <button onClick={onMoveUp} className="p-0.5 hover:bg-gray-100 rounded"><ChevronUp size={12} /></button>}
          {!isLast && <button onClick={onMoveDown} className="p-0.5 hover:bg-gray-100 rounded"><ChevronDown size={12} /></button>}
          <button onClick={() => setEditing(!editing)} className="p-0.5 hover:bg-gray-100 rounded"><Pencil size={12} className="text-gray-500" /></button>
          <button onClick={onDelete} className="p-0.5 hover:bg-red-50 rounded"><Trash2 size={12} className="text-red-400" /></button>
        </div>
      </div>
      {editing && (
        <div className="px-3 pb-3 border-t border-purple-200">
          <div className="mt-2">{renderEditFields()}</div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-xs text-gray-500">Anuluj</button>
            <button onClick={handleSave} className="px-3 py-1 text-xs bg-purple-600 text-white rounded">Zapisz</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== PAGE EDITOR ====================

function PageEditor({ page, onBack, toast, canEdit }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddBlock, setShowAddBlock] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get(`/cms/pages/${page.id}/blocks`); setBlocks(r.data.blocks); }
    catch {} finally { setLoading(false); }
  }, [page.id]);

  useEffect(() => { load(); }, [load]);

  const addBlock = async (type) => {
    const def = getBlockDef(type);
    try {
      await api.post(`/cms/pages/${page.id}/blocks`, { block_type: type, content: def.defaults || {} });
      setShowAddBlock(false); load();
    } catch { toast.error('Blad'); }
  };

  const saveBlock = async (blockId, content) => {
    try { await api.put(`/cms/blocks/${blockId}`, { content }); load(); }
    catch { toast.error('Blad zapisu'); }
  };

  const deleteBlock = async (blockId) => {
    try { await api.delete(`/cms/blocks/${blockId}`); load(); }
    catch { toast.error('Blad'); }
  };

  const moveBlock = async (index, direction) => {
    const ids = blocks.map(b => b.id);
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[index], ids[newIdx]] = [ids[newIdx], ids[index]];
    try { await api.put(`/cms/pages/${page.id}/blocks/reorder`, { blockIds: ids }); load(); }
    catch { toast.error('Blad'); }
  };

  const pageTypeIcons = { content: FileText, contact: Mail, shop: ShoppingBag };
  const PageIcon = pageTypeIcons[page.page_type] || FileText;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-3 flex items-center gap-1">
        ← Powrot do listy stron
      </button>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <PageIcon size={20} className="text-purple-600" />
          <h2 className="text-lg font-bold text-gray-800">{page.title}</h2>
          <span className="text-xs text-gray-400 font-mono">/{page.slug}</span>
          {page.is_homepage && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Strona glowna</span>}
        </div>
        {canEdit && (
          <button onClick={() => setShowAddBlock(true)} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">
            <Plus size={14} /> Dodaj blok
          </button>
        )}
      </div>

      {loading ? <div className="p-8 text-gray-400">Ladowanie...</div> : (
        <div className="space-y-2">
          {blocks.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>Strona pusta — dodaj pierwszy blok</p>
            </div>
          )}
          {blocks.map((block, i) => (
            <BlockEditor key={block.id} block={block}
              onSave={saveBlock} onDelete={() => deleteBlock(block.id)}
              onMoveUp={() => moveBlock(i, -1)} onMoveDown={() => moveBlock(i, 1)}
              isFirst={i === 0} isLast={i === blocks.length - 1} />
          ))}
        </div>
      )}

      <Modal isOpen={showAddBlock} onClose={() => setShowAddBlock(false)} title="Dodaj blok" maxWidth="max-w-xl">
        <div className="grid grid-cols-3 gap-2">
          {BLOCK_TYPES.map(bt => (
            <button key={bt.type} onClick={() => addBlock(bt.type)}
              className="flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-purple-50 hover:border-purple-300 transition text-center">
              <bt.icon size={24} className={`text-${bt.color}-500`} />
              <span className="text-xs font-medium text-gray-700">{bt.label}</span>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}

// ==================== MAIN CMS PAGE ====================

export default function CmsPage() {
  const { t } = useTranslation();
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const toast = useToast();
  const [tab, setTab] = useState('pages');
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showPageForm, setShowPageForm] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [deletePage, setDeletePage] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [siteRes, pagesRes] = await Promise.all([
        api.get('/cms/site'), api.get('/cms/pages')
      ]);
      setSite(siteRes.data.site);
      setPages(pagesRes.data.pages);
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSavePage = async (data) => {
    try {
      if (editingPage) await api.put(`/cms/pages/${editingPage.id}`, data);
      else await api.post('/cms/pages', data);
      setShowPageForm(false); setEditingPage(null); loadAll();
    } catch (err) { toast.error(err.response?.data?.error || t('common.error')); }
  };

  const handleDeletePage = async () => {
    try { await api.delete(`/cms/pages/${deletePage.id}`); setDeletePage(null); loadAll(); }
    catch { toast.error(t('common.error')); }
  };

  const handleSaveSite = async (data) => {
    try { await api.put('/cms/site', data); toast.success(t('cms.settingsSaved')); loadAll(); }
    catch { toast.error(t('common.error')); }
  };

  if (!canView('cms')) return <div className="p-8 text-center text-gray-400"><Globe size={48} className="mx-auto mb-4 opacity-50" /><p>{t('common.noAccess')}</p></div>;
  if (loading) return <div className="p-8 text-gray-400">{t('common.loading')}</div>;

  if (selectedPage) {
    return (
      <div className="p-6">
        <PageEditor page={selectedPage} onBack={() => { setSelectedPage(null); loadAll(); }} toast={toast} canEdit={canEdit('cms')} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <Globe size={28} className="text-purple-600" /> {t('cms.title')}
          </h1>
          <p className="text-gray-500 mt-1">{site?.site_name} — {site?.subdomain}.swibi.pl
            {site?.is_published ? <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Opublikowana</span> : <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Szkic</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`/site/${site?.subdomain || 'default'}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-sm">
            <ExternalLink size={14} /> Podgląd strony
          </a>
          <button onClick={() => setTab('settings')} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
            <Settings size={14} /> {t('cms.settings')}
          </button>
          {canCreate('cms') && (
            <button onClick={() => { setEditingPage(null); setShowPageForm(true); }} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">
              <Plus size={14} /> {t('cms.newPage')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs (badges) */}
      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { key: 'settings', label: t('cms.settings'), icon: Settings },
          { key: 'pages', label: 'Strony', icon: FileText },
          { key: 'menu', label: 'Menu', icon: Menu },
          { key: 'gallery', label: 'Galeria', icon: Image },
          { key: 'blog', label: 'Blog', icon: FileText },
          { key: 'ai', label: 'Kreator AI', icon: Sparkles },
          { key: 'publish', label: 'Publikacja', icon: UploadCloud },
        ].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition ${tab === tb.key ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            <tb.icon size={14} /> {tb.label}
          </button>
        ))}
      </div>

      {tab === 'menu' && <MenuEditor pages={pages} toast={toast} canEdit={canEdit('cms')} />}
      {tab === 'blog' && <BlogPanel toast={toast} canCreate={canCreate('cms')} canEdit={canEdit('cms')} canDelete={canDelete('cms')} />}
      {tab === 'gallery' && <GalleryPanel toast={toast} canCreate={canCreate('cms')} canEdit={canEdit('cms')} canDelete={canDelete('cms')} />}
      {tab === 'ai' && <AiWizard toast={toast} onApplied={() => { setTab('pages'); loadAll(); }} />}
      {tab === 'publish' && <DeployPanel toast={toast} canEdit={canEdit('cms')} />}
      {tab === 'settings' && (
        <div className="max-w-2xl">
          <h2 className="text-lg font-bold text-gray-800 mb-4">{t('cms.siteSettings')}</h2>
          <SiteSettingsForm site={site} toast={toast} onSave={handleSaveSite} onCancel={() => setTab('pages')} />
        </div>
      )}

      {/* Pages list */}
      {tab === 'pages' && <div className="space-y-2">
        {pages.length === 0 && <div className="text-center py-16 text-gray-400"><FileText size={48} className="mx-auto mb-4 opacity-50" /><p>{t('cms.noPages')}</p></div>}
        {pages.map(p => (
          <div key={p.id} onClick={() => setSelectedPage(p)}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer flex items-center justify-between group">
            <div className="flex items-center gap-3">
              {p.is_homepage ? <Home size={18} className="text-amber-500" /> : <FileText size={18} className="text-gray-400" />}
              <div>
                <div className="font-medium text-gray-800 flex items-center gap-2">
                  {p.title}
                  <span className="text-xs text-gray-400 font-mono">/{p.slug}</span>
                </div>
                <div className="text-xs text-gray-500">{p.blocks_count || 0} blokow · {p.page_type}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.is_published ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-gray-300" />}
              <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                {canEdit('cms') && <button onClick={e => { e.stopPropagation(); setEditingPage(p); setShowPageForm(true); }} className="p-1 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>}
                {canDelete('cms') && <button onClick={e => { e.stopPropagation(); setDeletePage(p); }} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>}

      {/* Page form modal */}
      <Modal isOpen={showPageForm} onClose={() => { setShowPageForm(false); setEditingPage(null); }}
        title={editingPage ? t('cms.editPage') : t('cms.newPage')}>
        <PageForm initial={editingPage} toast={toast} onSave={handleSavePage} onCancel={() => { setShowPageForm(false); setEditingPage(null); }} />
      </Modal>


      <ConfirmDialog isOpen={!!deletePage} type="danger" title={t('cms.deletePage')}
        message={t('cms.deletePageConfirm', { title: deletePage?.title })}
        onConfirm={handleDeletePage} onCancel={() => setDeletePage(null)} />
    </div>
  );
}

// ==================== PAGE FORM ====================

function PageForm({ initial, toast, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: initial?.title || '', slug: initial?.slug || '', page_type: initial?.page_type || 'content',
    is_homepage: initial?.is_homepage || false, is_published: initial?.is_published ?? true,
    meta_description: initial?.meta_description || '',
    h1: initial?.h1 || '', og_title: initial?.og_title || '', og_description: initial?.og_description || '',
    canonical_url: initial?.canonical_url || '', noindex: initial?.noindex || false,
  });
  const [showSeo, setShowSeo] = useState(false);
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const counter = (val, min, max) => {
    const n = (val || '').length;
    const color = n === 0 ? 'text-gray-400' : (n < min || n > max) ? 'text-amber-600' : 'text-green-600';
    return <span className={`text-xs ${color}`}>{n}/{max}</span>;
  };
  const [aiBusy, setAiBusy] = useState(false);
  const aiMeta = async () => {
    setAiBusy(true);
    try {
      const r = await api.post('/cms/ai/assist', { kind: 'field', payload: { field: 'meta description', instruction: '120–160 znaków, z wezwaniem do działania, po polsku.', context: `Strona: ${form.title}. H1: ${form.h1 || form.title}.`, current: form.meta_description } });
      if (r.data.text) setForm(f => ({ ...f, meta_description: r.data.text }));
    } catch (e) { toast?.error(e.response?.data?.error || 'Błąd AI'); }
    finally { setAiBusy(false); }
  };
  const generateSlug = (t) => t.toLowerCase().replace(/[ąàáâ]/g,'a').replace(/[ćč]/g,'c').replace(/[ęèéê]/g,'e')
    .replace(/[łĺ]/g,'l').replace(/[ńñ]/g,'n').replace(/[óòôõ]/g,'o').replace(/[śš]/g,'s').replace(/[żźž]/g,'z')
    .replace(/[ůúùû]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="space-y-3">
      <div><label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1"><span>Tytul (SEO &lt;title&gt;) *</span> {counter(form.title, 50, 60)}</label>
        <input type="text" value={form.title} onChange={e => setForm({...form, title: e.target.value, slug: initial ? form.slug : generateSlug(e.target.value)})} className={ic} required autoFocus /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL) *</label>
        <input type="text" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className={ic + " font-mono"} required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Typ strony</label>
          <select value={form.page_type} onChange={e => setForm({...form, page_type: e.target.value})} className={ic}>
            <option value="content">Tresc</option><option value="contact">Kontakt</option>
            <option value="shop">Sklep</option><option value="gallery">Galeria</option>
            <option value="blog_list">Lista bloga</option>
          </select></div>
        <div className="flex flex-col justify-end gap-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_homepage} onChange={e => setForm({...form, is_homepage: e.target.checked})} /> Strona glowna</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} /> Opublikowana</label>
        </div>
      </div>
      <div><label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1">
          <span className="flex items-center gap-2">Meta Description (SEO)
            <button type="button" onClick={aiMeta} disabled={aiBusy} className="text-purple-600 hover:underline disabled:opacity-50 inline-flex items-center gap-0.5 font-normal">{aiBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} AI</button>
          </span> {counter(form.meta_description, 120, 160)}</label>
        <textarea value={form.meta_description} onChange={e => setForm({...form, meta_description: e.target.value})} className={ic} rows={2} /></div>

      {/* Advanced SEO */}
      <button type="button" onClick={() => setShowSeo(s => !s)} className="text-xs text-purple-600 hover:underline flex items-center gap-1">
        <ChevronDown size={12} className={showSeo ? 'rotate-180 transition' : 'transition'} /> Zaawansowane SEO / Open Graph
      </button>
      {showSeo && (
        <div className="space-y-3 border border-gray-100 rounded-lg p-3 bg-gray-50">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Nagłówek H1 (jeśli pusty — użyty tytuł)</label>
            <input type="text" value={form.h1} onChange={e => setForm({...form, h1: e.target.value})} className={ic} placeholder="Główny nagłówek strony" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">OG title (social)</label>
            <input type="text" value={form.og_title} onChange={e => setForm({...form, og_title: e.target.value})} className={ic} placeholder="Domyślnie = tytuł" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">OG description (social)</label>
            <textarea value={form.og_description} onChange={e => setForm({...form, og_description: e.target.value})} className={ic} rows={2} placeholder="Domyślnie = meta description" /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Canonical URL (nadpisanie — rzadko)</label>
            <input type="text" value={form.canonical_url} onChange={e => setForm({...form, canonical_url: e.target.value})} className={ic + " font-mono"} placeholder="https://..." /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.noindex} onChange={e => setForm({...form, noindex: e.target.checked})} /> noindex (wyłącz z indeksowania i sitemap)</label>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">{initial ? 'Zapisz' : 'Utworz'}</button>
      </div>
    </form>
  );
}

// ==================== SITE SETTINGS FORM ====================

function SiteSettingsForm({ site, toast, onSave, onCancel }) {
  const [form, setForm] = useState({
    site_name: site?.site_name || '', site_description: site?.site_description || '',
    theme: site?.theme || 'modern', primary_color: site?.primary_color || '#2563EB',
    secondary_color: site?.secondary_color || '#059669', font_family: site?.font_family || 'Inter',
    domain: site?.domain || '', is_published: site?.is_published || false, analytics_id: site?.analytics_id || '',
    canonical_host: site?.canonical_host || '', lang: site?.lang || 'pl', ga4_id: site?.ga4_id || '',
    business_phone: site?.business_phone || '', business_email: site?.business_email || '',
    business_street: site?.business_street || '', business_city: site?.business_city || '',
    business_postal_code: site?.business_postal_code || '', business_nip: site?.business_nip || '',
    logo_filename: site?.logo_filename || '', favicon_filename: site?.favicon_filename || '', og_default_filename: site?.og_default_filename || '',
  });
  const [socialText, setSocialText] = useState(Array.isArray(site?.social_links) ? site.social_links.join('\n') : '');
  const [uploading, setUploading] = useState('');
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const submit = (e) => {
    e.preventDefault();
    const social_links = socialText.split('\n').map(s => s.trim()).filter(Boolean);
    onSave({ ...form, social_links });
  };

  const uploadImage = async (field, file) => {
    if (!file) return;
    setUploading(field);
    const fd = new FormData(); fd.append('file', file);
    try {
      const r = await api.post('/cms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, [field]: r.data.filename }));
    } catch (err) { toast?.error(err.response?.data?.error || 'Błąd wgrywania'); }
    finally { setUploading(''); }
  };

  const imgField = (label, field, hint) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="flex items-center gap-3">
        {form[field]
          ? <img src={mediaUrl(form[field])} alt="" className="h-12 max-w-[120px] object-contain border rounded bg-gray-50 p-1" />
          : <div className="h-12 w-20 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-300 text-xs">brak</div>}
        <input type="file" accept="image/*" onChange={e => uploadImage(field, e.target.files[0])} className="text-xs" />
        {uploading === field && <span className="text-xs text-gray-400">Wgrywanie…</span>}
        {form[field] && <button type="button" onClick={() => setForm(f => ({ ...f, [field]: '' }))} className="text-xs text-red-500 hover:underline">Usuń</button>}
      </div>
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3">
      {/* Media */}
      <div className="border border-gray-100 rounded-lg p-3 bg-gray-50 space-y-3">
        <div className="text-xs font-semibold text-gray-600">Logo i grafiki</div>
        {imgField('Logo strony', 'logo_filename', 'Wyświetlane w nagłówku i stopce.')}
        {imgField('Favicon', 'favicon_filename', 'Ikona w karcie przeglądarki.')}
        {imgField('Domyślny obraz OG (social)', 'og_default_filename', 'Zalecane 1200×630. Używany w podglądach na social media.')}
      </div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Nazwa strony</label>
        <input type="text" value={form.site_name} onChange={e => setForm({...form, site_name: e.target.value})} className={ic} /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Opis strony (SEO)</label>
        <textarea value={form.site_description} onChange={e => setForm({...form, site_description: e.target.value})} className={ic} rows={2} /></div>
      <div className="grid grid-cols-3 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Motyw</label>
          <select value={form.theme} onChange={e => setForm({...form, theme: e.target.value})} className={ic}>
            <option value="modern">Modern</option><option value="classic">Classic</option><option value="minimal">Minimal</option>
          </select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Kolor glowny</label>
          <input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="w-full h-10 rounded border" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Kolor dodatkowy</label>
          <input type="color" value={form.secondary_color} onChange={e => setForm({...form, secondary_color: e.target.value})} className="w-full h-10 rounded border" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Czcionka</label>
          <select value={form.font_family} onChange={e => setForm({...form, font_family: e.target.value})} className={ic}>
            <option value="Inter">Inter</option><option value="Roboto">Roboto</option><option value="Lato">Lato</option>
            <option value="Open Sans">Open Sans</option><option value="Poppins">Poppins</option>
          </select></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Wlasna domena</label>
          <input type="text" value={form.domain} onChange={e => setForm({...form, domain: e.target.value})} className={ic} placeholder="www.mojafirma.pl" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Kanoniczny host (bez https://)</label>
          <input type="text" value={form.canonical_host} onChange={e => setForm({...form, canonical_host: e.target.value})} className={ic + " font-mono"} placeholder="firma.swibi.pl lub www.firma.pl" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">GA4 ID (Consent Mode v2)</label>
          <input type="text" value={form.ga4_id} onChange={e => setForm({...form, ga4_id: e.target.value})} className={ic} placeholder="G-XXXXXXXXXX" /></div>
      </div>
      <p className="text-xs text-gray-400 -mt-1">Kanoniczny host jest używany w &lt;link canonical&gt;, og:url i sitemap. Ustaw go zgodnie z domeną docelową na serwerze FTP.</p>

      {/* Dane firmy (JSON-LD LocalBusiness + stopka) */}
      <div className="border border-gray-100 rounded-lg p-3 bg-gray-50 space-y-3">
        <div className="text-xs font-semibold text-gray-600">Dane firmy (do danych strukturalnych i stopki)</div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Telefon</label>
            <input type="text" value={form.business_phone} onChange={e => setForm({...form, business_phone: e.target.value})} className={ic} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Email kontaktowy</label>
            <input type="email" value={form.business_email} onChange={e => setForm({...form, business_email: e.target.value})} className={ic} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Ulica i nr</label>
            <input type="text" value={form.business_street} onChange={e => setForm({...form, business_street: e.target.value})} className={ic} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Kod</label>
              <input type="text" value={form.business_postal_code} onChange={e => setForm({...form, business_postal_code: e.target.value})} className={ic} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Miasto</label>
              <input type="text" value={form.business_city} onChange={e => setForm({...form, business_city: e.target.value})} className={ic} /></div>
          </div>
        </div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">NIP</label>
          <input type="text" value={form.business_nip} onChange={e => setForm({...form, business_nip: e.target.value})} className={ic} placeholder="bez prefiksu PL" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Profile społecznościowe (sameAs) — jeden URL na linię</label>
          <textarea value={socialText} onChange={e => setSocialText(e.target.value)} className={ic + " font-mono"} rows={2} placeholder="https://facebook.com/...&#10;https://linkedin.com/company/..." /></div>
      </div>

      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={e => setForm({...form, is_published: e.target.checked})} /> Strona opublikowana</label>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">Zapisz</button>
      </div>
    </form>
  );
}

// ==================== DEPLOY PANEL (FTP) ====================

function DeployPanel({ toast, canEdit }) {
  const [cfg, setCfg] = useState(null);
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testModal, setTestModal] = useState(null); // { status:'connecting'|'ok'|'error', message }
  const [deploy, setDeploy] = useState(null);        // { pct, phase, phases:[], result:{ok,error,stats}|null }
  const [precheck, setPrecheck] = useState(null);    // { loading, items:[], path, error }
  const testing = testModal?.status === 'connecting';
  const deploying = deploy && !deploy.result;

  const load = useCallback(async () => {
    try { const r = await api.get('/cms/site'); setCfg(r.data.site); }
    catch { toast.error('Błąd ładowania'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 text-gray-400">Ładowanie...</div>;
  if (!cfg) return null;

  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const defaultPort = cfg.deploy_protocol === 'sftp' ? 22 : 21;

  const payload = () => ({
    deploy_protocol: cfg.deploy_protocol || 'sftp',
    deploy_host: cfg.deploy_host || '',
    deploy_port: cfg.deploy_port || '',
    deploy_username: cfg.deploy_username || '',
    deploy_remote_path: cfg.deploy_remote_path || '/',
    public_api_url: cfg.public_api_url || '',
    ...(pwd ? { deploy_password: pwd } : {}),
  });

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await api.put('/cms/deploy/settings', payload());
      setCfg(r.data.site); setPwd('');
      toast.success('Ustawienia zapisane');
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd zapisu'); }
    finally { setSaving(false); }
  };

  const testConn = async () => {
    setTestModal({ status: 'connecting', message: 'Łączenie z serwerem…' });
    try {
      await api.post('/cms/deploy/test', payload());
      setTestModal({ status: 'ok', message: 'Połączenie nawiązane poprawnie.' });
    } catch (e) {
      setTestModal({ status: 'error', message: e.response?.data?.error || 'Połączenie nieudane.' });
    }
  };

  // Step 1: check what already exists on the server + detect PHP/SSH (for archiving)
  const publish = async () => {
    setPrecheck({ loading: true, items: [], path: '', error: null });
    try {
      const r = await api.post('/cms/deploy/remote-list', payload());
      const items = r.data.items || [];
      const caps = { php: !!r.data.php, phpVersion: r.data.phpVersion || null, ssh: !!r.data.ssh };
      const path = r.data.path || '';
      const renamable = !!(path && path.replace(/\/+$/, '') !== '');
      if (items.length === 0) { setPrecheck(null); runDeploy('none'); }   // nothing there → go straight
      else {
        const mode = renamable ? 'rename' : ((caps.php || caps.ssh) ? 'zip' : 'direct');
        setPrecheck({ loading: false, items, path, error: null, ...caps, renamable, mode });
      }
    } catch (e) {
      setPrecheck({ loading: false, items: [], path: '', error: e.response?.data?.error || 'Nie udało się sprawdzić serwera', php: false, ssh: false, renamable: false, mode: 'direct' });
    }
  };

  // Step 2: actual streaming upload (backupMode: 'rename' | 'zip' | 'none')
  const runDeploy = async (backupMode = 'none') => {
    setPrecheck(null);
    setDeploy({ pct: 0, phase: 'Rozpoczynanie…', phases: [], result: null });
    try {
      const base = (api.defaults.baseURL || '/api').replace(/\/$/, '');
      const token = localStorage.getItem('token');
      const res = await fetch(`${base}/cms/deploy`, {
        method: 'POST',
        headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupMode }),
      });
      if (!res.ok && !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 1);
          if (!line) continue;
          let evt; try { evt = JSON.parse(line); } catch { continue; }
          if (evt.type === 'progress') {
            setDeploy(d => ({ ...d, pct: evt.pct ?? d.pct, phase: evt.message || d.phase, phases: [...d.phases, evt.message].filter(Boolean) }));
          } else if (evt.type === 'done') {
            setDeploy(d => ({
              ...d,
              pct: evt.ok ? 100 : d.pct,
              phase: evt.ok ? 'Publikacja zakończona.' : (evt.error || 'Błąd publikacji'),
              phases: (evt.log && evt.log.length) ? evt.log : d.phases,
              result: { ok: evt.ok, error: evt.error, stats: evt.stats },
            }));
            if (evt.ok) toast.success(`Opublikowano ${evt.stats?.pages || 0} stron`);
            else toast.error(evt.error || 'Błąd publikacji');
            load();
          }
        }
      }
    } catch (e) {
      setDeploy(d => ({ ...(d || { pct: 0, phases: [] }), phase: 'Błąd połączenia', result: { ok: false, error: e.message } }));
      toast.error('Błąd publikacji: ' + e.message);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Status */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {cfg.last_deploy_status === 'success' ? <CheckCircle size={20} className="text-green-500" />
            : cfg.last_deploy_status === 'error' ? <XCircle size={20} className="text-red-500" />
            : <Server size={20} className="text-gray-400" />}
          <div>
            <div className="text-sm font-medium text-gray-800">
              {cfg.last_deployed_at ? `Ostatnia publikacja: ${new Date(cfg.last_deployed_at).toLocaleString('pl-PL')}` : 'Jeszcze nie opublikowano'}
            </div>
            <div className="text-xs text-gray-500">{cfg.last_deploy_status === 'success' ? 'Sukces' : cfg.last_deploy_status === 'error' ? 'Błąd — sprawdź log' : '—'}</div>
          </div>
        </div>
        {canEdit && (
          <button onClick={publish} disabled={deploying || !cfg.deploy_host}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium">
            <UploadCloud size={16} /> {deploying ? 'Publikowanie...' : 'Publikuj na serwer'}
          </button>
        )}
      </div>

      {/* FTP settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-4"><Server size={16} /> Serwer docelowy (FTP / SFTP)</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Protokół</label>
              <select value={cfg.deploy_protocol || 'sftp'}
                onChange={e => {
                  const proto = e.target.value;
                  setCfg(c => {
                    const cur = c.deploy_port;
                    const isDefault = !cur || String(cur) === '21' || String(cur) === '22';
                    return { ...c, deploy_protocol: proto, deploy_port: isDefault ? (proto === 'sftp' ? 22 : 21) : cur };
                  });
                }}
                className={ic} disabled={!canEdit}>
                <option value="sftp">SFTP (SSH)</option><option value="ftps">FTPS (FTP+TLS)</option><option value="ftp">FTP (nieszyfrowany)</option>
              </select></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Host</label>
              <input type="text" value={cfg.deploy_host || ''} onChange={e => set('deploy_host', e.target.value)} className={ic} placeholder="ftp.twojadomena.pl" disabled={!canEdit} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Port</label>
              <input type="number" value={cfg.deploy_port || ''} onChange={e => set('deploy_port', e.target.value)} className={ic} placeholder={String(defaultPort)} disabled={!canEdit} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Użytkownik</label>
              <input type="text" value={cfg.deploy_username || ''} onChange={e => set('deploy_username', e.target.value)} className={ic} disabled={!canEdit} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Hasło {cfg.deploy_password_set && <span className="text-green-600">(ustawione)</span>}</label>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} className={ic} placeholder={cfg.deploy_password_set ? '•••••• (bez zmian)' : ''} disabled={!canEdit} /></div>
          </div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Katalog zdalny</label>
            <input type="text" value={cfg.deploy_remote_path || ''} onChange={e => set('deploy_remote_path', e.target.value)} className={ic + " font-mono"} placeholder="/ lub /public_html" disabled={!canEdit} /></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Adres API SWIBI (dla formularzy na stronie)</label>
            <input type="text" value={cfg.public_api_url || ''} onChange={e => set('public_api_url', e.target.value)} className={ic + " font-mono"} placeholder="https://api.swibi.pl" disabled={!canEdit} />
            <p className="text-xs text-gray-400 mt-1">Statyczna strona wysyła formularze na ten adres. Wymaga, by host SWIBI był publicznie dostępny.</p></div>

          {canEdit && (
            <div className="flex gap-2 pt-1">
              <button onClick={saveSettings} disabled={saving} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm">{saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}</button>
              <button onClick={testConn} disabled={testing || !cfg.deploy_host} className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 rounded-lg text-sm">{testing ? 'Testowanie...' : 'Testuj połączenie'}</button>
            </div>
          )}
        </div>
      </div>

      {/* Last deploy log (persisted) */}
      {cfg.last_deploy_log && (
        <details className="bg-gray-900 text-gray-200 rounded-lg p-4 text-xs">
          <summary className="cursor-pointer text-gray-400 mb-2">Log ostatniej publikacji</summary>
          <div className="font-mono whitespace-pre-wrap max-h-72 overflow-auto">{cfg.last_deploy_log}</div>
        </details>
      )}

      {/* Test connection modal */}
      <Modal isOpen={!!testModal} onClose={() => testModal?.status !== 'connecting' && setTestModal(null)} title="Test połączenia" maxWidth="max-w-md">
        {testModal && (
          <div className="text-center py-4">
            {testModal.status === 'connecting' && <Loader2 size={40} className="mx-auto mb-4 text-purple-600 animate-spin" />}
            {testModal.status === 'ok' && <CheckCircle size={44} className="mx-auto mb-4 text-green-500" />}
            {testModal.status === 'error' && <XCircle size={44} className="mx-auto mb-4 text-red-500" />}
            <p className={`text-sm ${testModal.status === 'error' ? 'text-red-600' : testModal.status === 'ok' ? 'text-green-700' : 'text-gray-600'}`}>{testModal.message}</p>
            {testModal.status !== 'connecting' && (
              <button onClick={() => setTestModal(null)} className="mt-5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">Zamknij</button>
            )}
          </div>
        )}
      </Modal>

      {/* Pre-deploy check modal — existing files on the server */}
      <Modal isOpen={!!precheck} onClose={() => !precheck?.loading && setPrecheck(null)} title="Sprawdzanie serwera" maxWidth="max-w-lg">
        {precheck && (
          <div>
            {precheck.loading ? (
              <div className="text-center py-6">
                <Loader2 size={36} className="mx-auto mb-3 text-purple-600 animate-spin" />
                <p className="text-sm text-gray-600">Sprawdzanie zawartości katalogu docelowego…</p>
              </div>
            ) : precheck.error ? (
              <div>
                <div className="flex items-start gap-2 text-red-600 text-sm mb-4"><XCircle size={18} className="flex-shrink-0 mt-0.5" /> {precheck.error}</div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setPrecheck(null)} className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm">Anuluj</button>
                  <button onClick={() => runDeploy('none')} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">Publikuj mimo to</button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-700 mb-1">
                  Katalog <span className="font-mono text-gray-900">{precheck.path}</span> nie jest pusty — zawiera <b>{precheck.items.length}</b> {precheck.items.length === 1 ? 'element' : 'elementów'}:
                </p>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-auto mb-4">
                  {precheck.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                      <FileText size={14} className={`flex-shrink-0 ${it.type === 'dir' ? 'text-amber-500' : 'text-gray-400'}`} />
                      <span className={`flex-1 truncate ${it.type === 'dir' ? 'font-medium text-gray-800' : 'text-gray-600'}`}>{it.name}{it.type === 'dir' ? '/' : ''}</span>
                      {it.type !== 'dir' && it.size != null && <span className="text-xs text-gray-400">{(it.size / 1024).toFixed(1)} KB</span>}
                    </div>
                  ))}
                </div>

                {/* Backup mode selection */}
                <div className="space-y-2 mb-4">
                  <div className="text-xs font-semibold text-gray-600">Co zrobić z istniejącą zawartością?</div>

                  <label className={`flex items-start gap-2 p-3 border rounded-lg cursor-pointer ${precheck.mode === 'rename' ? 'border-purple-300 bg-purple-50' : 'border-gray-200'} ${!precheck.renamable ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input type="radio" name="bmode" disabled={!precheck.renamable} checked={precheck.mode === 'rename'} onChange={() => setPrecheck(p => ({ ...p, mode: 'rename' }))} className="mt-0.5" />
                    <span className="text-sm text-gray-700">
                      <b>Zmień nazwę starego folderu i opublikuj na czysto</b> <span className="text-green-600 text-xs">(zalecane)</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {precheck.renamable
                          ? <>Istniejący folder zostanie przeniesiony na <span className="font-mono">{precheck.path.replace(/\/+$/, '')}_archiwum_RRRR-MM-DD_GG-MM</span>, a strona trafi do nowego, pustego folderu. Działa na każdym serwerze.</>
                          : <>Niedostępne — katalog docelowy to katalog główny (/). Wskaż podfolder (np. <span className="font-mono">/public_html</span>).</>}
                      </span>
                    </span>
                  </label>

                  {(precheck.php || precheck.ssh) && (
                    <label className={`flex items-start gap-2 p-3 border rounded-lg cursor-pointer ${precheck.mode === 'zip' ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
                      <input type="radio" name="bmode" checked={precheck.mode === 'zip'} onChange={() => setPrecheck(p => ({ ...p, mode: 'zip' }))} className="mt-0.5" />
                      <span className="text-sm text-gray-700">
                        <b>Zarchiwizuj do ZIP</b> (<span className="font-mono">_archive/</span>) i publikuj
                        <span className="block text-xs text-gray-500 mt-0.5">Kopia przez {precheck.ssh ? 'SSH' : `PHP${precheck.phpVersion ? ' ' + precheck.phpVersion : ''}`}. Folder <span className="font-mono">_archive</span> chroniony przed dostępem z URL. Pliki nadpisywane, pozostałe zostają.</span>
                      </span>
                    </label>
                  )}

                  <label className={`flex items-start gap-2 p-3 border rounded-lg cursor-pointer ${precheck.mode === 'direct' ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
                    <input type="radio" name="bmode" checked={precheck.mode === 'direct'} onChange={() => setPrecheck(p => ({ ...p, mode: 'direct' }))} className="mt-0.5" />
                    <span className="text-sm text-gray-700">
                      <b>Publikuj bezpośrednio</b>
                      <span className="block text-xs text-amber-600 mt-0.5">Nadpisze pliki o tych samych nazwach. Pozostałe pliki nie zostaną usunięte. Bez kopii.</span>
                    </span>
                  </label>
                </div>

                <div className="flex justify-end gap-2">
                  <button onClick={() => setPrecheck(null)} className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg text-sm">Anuluj</button>
                  <button onClick={() => runDeploy(precheck.mode === 'direct' ? 'none' : precheck.mode)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex items-center gap-2">
                    <UploadCloud size={15} /> {precheck.mode === 'rename' ? 'Przenieś i publikuj' : precheck.mode === 'zip' ? 'Archiwizuj i publikuj' : 'Publikuj'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Deploy progress modal */}
      <Modal isOpen={!!deploy} onClose={() => deploy?.result && setDeploy(null)} title="Publikacja strony" maxWidth="max-w-lg">
        {deploy && (
          <div>
            <div className="flex items-center gap-3 mb-3">
              {!deploy.result ? <Loader2 size={20} className="text-purple-600 animate-spin flex-shrink-0" />
                : deploy.result.ok ? <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                : <XCircle size={20} className="text-red-500 flex-shrink-0" />}
              <span className={`text-sm font-medium ${deploy.result && !deploy.result.ok ? 'text-red-600' : 'text-gray-800'}`}>{deploy.phase}</span>
              <span className="ml-auto text-xs text-gray-400">{deploy.pct}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
              <div className={`h-full transition-all duration-300 ${deploy.result && !deploy.result.ok ? 'bg-red-500' : 'bg-purple-600'}`} style={{ width: `${deploy.pct}%` }} />
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 max-h-56 overflow-auto space-y-1">
              {deploy.phases.map((p, i) => (
                <div key={i} className="text-xs text-gray-600 flex items-start gap-2">
                  <CheckCircle size={12} className="text-green-400 mt-0.5 flex-shrink-0" /> {p}
                </div>
              ))}
              {deploy.result && !deploy.result.ok && deploy.result.error && (
                <div className="text-xs text-red-600 flex items-start gap-2"><XCircle size={12} className="mt-0.5 flex-shrink-0" /> {deploy.result.error}</div>
              )}
            </div>
            {deploy.result && (
              <div className="flex justify-end mt-4">
                <button onClick={() => setDeploy(null)} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm">Zamknij</button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ==================== GALLERY PANEL ====================

function GalleryPanel({ toast, canCreate, canEdit, canDelete }) {
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagSearch, setTagSearch] = useState('');
  const [catForm, setCatForm] = useState(null);   // {} (new) | category (edit) | null
  const [itemForm, setItemForm] = useState(null);
  const [delCat, setDelCat] = useState(null);
  const [delItem, setDelItem] = useState(null);

  const loadCats = useCallback(async () => {
    try {
      const r = await api.get('/cms/gallery/categories');
      setCats(r.data.categories);
      setSel(s => (s ? r.data.categories.find(c => c.id === s.id) : null) || r.data.categories[0] || null);
    } catch { toast.error('Błąd ładowania'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { loadCats(); }, [loadCats]);

  const loadItems = useCallback(async () => {
    if (!sel) { setItems([]); return; }
    try { const r = await api.get(`/cms/gallery/items?category_id=${sel.id}`); setItems(r.data.items); }
    catch { toast.error('Błąd'); }
  }, [sel, toast]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const saveCat = async (data) => {
    try {
      if (catForm && catForm.id) await api.put(`/cms/gallery/categories/${catForm.id}`, data);
      else await api.post('/cms/gallery/categories', data);
      setCatForm(null); loadCats(); toast.success('Zapisano kategorię');
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd zapisu'); }
  };
  const removeCat = async () => { try { await api.delete(`/cms/gallery/categories/${delCat.id}`); setDelCat(null); setSel(null); loadCats(); } catch { toast.error('Błąd'); } };
  const saveItem = async (data) => {
    try {
      if (itemForm && itemForm.id) await api.put(`/cms/gallery/items/${itemForm.id}`, data);
      else await api.post('/cms/gallery/items', data);
      setItemForm(null); loadItems(); loadCats(); toast.success('Zapisano wpis');
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd zapisu'); }
  };
  const removeItem = async () => { try { await api.delete(`/cms/gallery/items/${delItem.id}`); setDelItem(null); loadItems(); loadCats(); } catch { toast.error('Błąd'); } };

  if (loading) return <div className="p-8 text-gray-400">Ładowanie…</div>;

  const shown = tagSearch.trim()
    ? items.filter(it => (it.tags || []).some(t => t.includes(tagSearch.trim().toLowerCase())))
    : items;

  return (
    <div className="flex gap-5">
      {/* Categories */}
      <div className="w-64 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-gray-700">Kategorie</h3>
          {canCreate && <button onClick={() => setCatForm({})} className="text-purple-600 hover:bg-purple-50 rounded p-1"><Plus size={16} /></button>}
        </div>
        {cats.length === 0 && <p className="text-xs text-gray-400">Brak kategorii — dodaj pierwszą.</p>}
        <div className="space-y-1">
          {cats.map(c => (
            <div key={c.id} onClick={() => setSel(c)}
              className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer ${sel?.id === c.id ? 'bg-purple-50 text-purple-700' : 'hover:bg-gray-50 text-gray-700'}`}>
              <span className="flex-1 text-sm truncate">{c.name}</span>
              <span className="text-xs text-gray-400">{c.items_count}</span>
              {canEdit && <button onClick={e => { e.stopPropagation(); setCatForm(c); }} className="opacity-0 group-hover:opacity-100 p-0.5"><Pencil size={12} className="text-gray-500" /></button>}
              {canDelete && <button onClick={e => { e.stopPropagation(); setDelCat(c); }} className="opacity-0 group-hover:opacity-100 p-0.5"><Trash2 size={12} className="text-red-400" /></button>}
            </div>
          ))}
        </div>
      </div>

      {/* Items */}
      <div className="flex-1 min-w-0">
        {!sel ? <div className="text-gray-400 text-sm p-8 text-center">Wybierz lub utwórz kategorię.</div> : (
          <>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-800 truncate">{sel.name}</h2>
                {sel.description && <p className="text-xs text-gray-500 truncate">{sel.description}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <input value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Szukaj po tagu…" className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-40" />
                {canCreate && <button onClick={() => setItemForm({})} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"><Plus size={14} /> Wpis</button>}
              </div>
            </div>

            {shown.length === 0 && <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg"><Image size={32} className="mx-auto mb-2 opacity-50" /><p>{tagSearch ? 'Brak wpisów dla tego tagu' : 'Brak wpisów — dodaj pierwszy'}</p></div>}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {shown.map(it => (
                <div key={it.id} className="border border-gray-200 rounded-lg overflow-hidden group bg-white">
                  {it.image?.src ? <img src={mediaUrl(it.image.src)} alt={it.alt || ''} className="w-full h-32 object-cover" /> : <div className="w-full h-32 bg-gray-100" />}
                  <div className="p-2">
                    <div className="text-sm font-medium text-gray-800 truncate">{it.meta?.company || it.meta?.location || it.alt || 'Wpis'}</div>
                    <div className="text-xs text-gray-400 truncate">{(it.tags || []).map(t => '#' + t).join(' ')}</div>
                    <div className="flex justify-end gap-1 mt-1 opacity-0 group-hover:opacity-100">
                      {canEdit && <button onClick={() => setItemForm(it)} className="p-1 hover:bg-gray-100 rounded"><Pencil size={13} className="text-gray-500" /></button>}
                      {canDelete && <button onClick={() => setDelItem(it)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={13} className="text-red-400" /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <Modal isOpen={!!catForm} onClose={() => setCatForm(null)} title={catForm?.id ? 'Edytuj kategorię' : 'Nowa kategoria'} maxWidth="max-w-lg">
        <GalleryCategoryForm initial={catForm} onSave={saveCat} onCancel={() => setCatForm(null)} />
      </Modal>
      <Modal isOpen={!!itemForm} onClose={() => setItemForm(null)} title={itemForm?.id ? 'Edytuj wpis' : 'Nowy wpis'} maxWidth="max-w-lg">
        {sel && <GalleryItemForm category={sel} initial={itemForm} toast={toast} onSave={saveItem} onCancel={() => setItemForm(null)} />}
      </Modal>
      <ConfirmDialog isOpen={!!delCat} type="danger" title="Usuń kategorię" message={`Usunąć kategorię „${delCat?.name}" wraz ze wszystkimi wpisami?`} onConfirm={removeCat} onCancel={() => setDelCat(null)} />
      <ConfirmDialog isOpen={!!delItem} type="danger" title="Usuń wpis" message="Usunąć ten wpis z galerii?" onConfirm={removeItem} onCancel={() => setDelItem(null)} />
    </div>
  );
}

function GalleryCategoryForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || '');
  const [slug, setSlug] = useState(initial?.slug || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [fields, setFields] = useState(Array.isArray(initial?.fields) && initial.fields.length ? initial.fields : [
    { key: 'company', label: 'Nazwa firmy' }, { key: 'location', label: 'Miejscowość' },
    { key: 'description', label: 'Opis' }, { key: 'dimensions', label: 'Wymiary' }, { key: 'extra', label: 'Dodatkowe informacje' },
  ]);
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const keyFromLabel = (l) => l.toLowerCase().replace(/[ąàáâ]/g,'a').replace(/[ćč]/g,'c').replace(/[ęèéê]/g,'e').replace(/[łĺ]/g,'l').replace(/[ńñ]/g,'n').replace(/[óòôõ]/g,'o').replace(/[śš]/g,'s').replace(/[żźž]/g,'z').replace(/[ůúùû]/g,'u').replace(/[^a-z0-9]+/g,'_').replace(/^_|_$/g,'');
  const setField = (i, label) => setFields(fields.map((f, idx) => idx === i ? { key: f.key || keyFromLabel(label), label } : f));
  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ name, slug, description, fields: fields.filter(f => f.label.trim()).map(f => ({ key: f.key || keyFromLabel(f.label), label: f.label })) }); }} className="space-y-3">
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Nazwa kategorii *</label>
        <input value={name} onChange={e => setName(e.target.value)} className={ic} required autoFocus /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL)</label>
        <input value={slug} onChange={e => setSlug(e.target.value)} className={ic + ' font-mono'} placeholder="auto z nazwy" /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Opis kategorii (SEO)</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} className={ic} rows={2} /></div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Pola opisowe wpisów (do SEO)</label>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={f.label} onChange={e => setField(i, e.target.value)} placeholder="Etykieta pola" className={ic} />
              <span className="text-xs text-gray-400 font-mono w-24 truncate">{f.key || keyFromLabel(f.label)}</span>
              <button type="button" onClick={() => setFields(fields.filter((_, idx) => idx !== i))} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setFields([...fields, { key: '', label: '' }])} className="text-xs text-purple-600 hover:underline flex items-center gap-1"><Plus size={12} /> Dodaj pole</button>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">Zapisz</button>
      </div>
    </form>
  );
}

function GalleryItemForm({ category, initial, toast, onSave, onCancel }) {
  const [image, setImage] = useState(initial?.image || null);
  const [alt, setAlt] = useState(initial?.alt || '');
  const [tags, setTags] = useState((initial?.tags || []).join(', '));
  const [meta, setMeta] = useState(initial?.meta || {});
  const [seoName, setSeoName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lat, setLat] = useState(initial?.lat ?? '');
  const [lng, setLng] = useState(initial?.lng ?? '');
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const fields = Array.isArray(category.fields) ? category.fields : [];

  const suggestSeo = () => [category.name, meta.location, meta.company, ...tags.split(',').map(t => t.trim())].filter(Boolean).join(' ');

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('seoName', (seoName || suggestSeo() || category.name));
    try {
      const r = await api.post('/cms/gallery/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImage({ src: r.data.filename, webp: r.data.webp, variants: r.data.variants, width: r.data.width, height: r.data.height });
    } catch (e) { toast?.error('Błąd wgrywania'); }
    finally { setUploading(false); }
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSave({ category_id: category.id, image: image || {}, alt, tags: tags.split(',').map(t => t.trim()).filter(Boolean), meta, lat, lng }); }} className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Nazwa pliku (SEO) — wzór: opis-miejscowosc-firma</label>
        <input value={seoName} onChange={e => setSeoName(e.target.value)} className={ic + ' font-mono'} placeholder={suggestSeo() || 'np. pylon-reklamowy-ostrow-wielkopolski-firma'} />
        <p className="text-xs text-gray-400 mt-1">Plik zostanie zapisany jako <span className="font-mono">{(seoName || suggestSeo() || 'galeria').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'galeria'}_N.webp</span></p>
      </div>
      <div className="flex items-center gap-3">
        {image?.src ? <img src={mediaUrl(image.src)} alt="" className="h-20 w-28 object-cover rounded border" /> : <div className="h-20 w-28 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-300 text-xs">brak</div>}
        <div>
          <input type="file" accept="image/*" onChange={e => upload(e.target.files[0])} className="text-xs" />
          {uploading && <div className="text-xs text-gray-400 mt-1">Wgrywanie…</div>}
          {image?.width && <div className="text-xs text-gray-400 mt-1">{image.width}×{image.height}px{image.variants ? ` · ${image.variants.length} WebP` : ''}</div>}
        </div>
      </div>
      {/* Per-category descriptive fields */}
      {fields.map(f => (
        <div key={f.key}>
          <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
          {f.key === 'description' || f.key === 'extra'
            ? <textarea value={meta[f.key] || ''} onChange={e => setMeta({ ...meta, [f.key]: e.target.value })} className={ic} rows={2} />
            : <input value={meta[f.key] || ''} onChange={e => setMeta({ ...meta, [f.key]: e.target.value })} className={ic} />}
        </div>
      ))}
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Tagi (po przecinku — do wyszukiwania)</label>
        <input value={tags} onChange={e => setTags(e.target.value)} className={ic} placeholder="pylon, podświetlany, wolnostojący" /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Tekst alternatywny (alt)</label>
        <input value={alt} onChange={e => setAlt(e.target.value)} className={ic} placeholder="Opis obrazu dla SEO/dostępności" /></div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Lokalizacja na mapie realizacji (opcjonalnie)</label>
        <div className="grid grid-cols-2 gap-2">
          <input value={lat} onChange={e => setLat(e.target.value)} className={ic} placeholder="Szerokość (lat), np. 51.6500" inputMode="decimal" />
          <input value={lng} onChange={e => setLng(e.target.value)} className={ic} placeholder="Długość (lng), np. 17.8100" inputMode="decimal" />
        </div>
        <p className="text-xs text-gray-400 mt-1">Skopiuj z Google Maps (prawy klik na punkcie → współrzędne). Wpisy ze współrzędnymi pojawią się jako pinezki w bloku „Mapa realizacji".</p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" disabled={!image?.src} className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg">Zapisz</button>
      </div>
    </form>
  );
}

// ==================== AI WEBSITE WIZARD ====================

function AiWizard({ toast, onApplied }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [applying, setApplying] = useState(false);
  const [started, setStarted] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(`${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const abortRef = useRef(null);
  const [usage, setUsage] = useState(null);
  const [planDesc, setPlanDesc] = useState('');
  useEffect(() => { if (plan) setPlanDesc(plan.description || plan.notes || ''); }, [plan]);
  const [archive, setArchive] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [delSession, setDelSession] = useState(null);

  const loadArchive = useCallback(async () => {
    try { const r = await api.get('/cms/ai/logs'); setArchive(r.data.sessions || []); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadArchive(); }, [loadArchive]);
  const openSession = async (id) => { try { const r = await api.get(`/cms/ai/logs/${id}`); setViewing(r.data.session); } catch { toast.error('Błąd odczytu rozmowy'); } };
  const removeSession = async () => { try { await api.delete(`/cms/ai/logs/${delSession}`); setDelSession(null); setViewing(null); loadArchive(); } catch { toast.error('Błąd'); } };
  const usePlanFromSession = (session) => { if (session?.plan) { setViewing(null); setPlan(session.plan); } };
  const resumeSession = (session) => {
    if (!session) return;
    const msgs = (session.messages || []).filter(m => m && m.role && typeof m.content === 'string' && m.content !== '[Wygenerowano plan strony]');
    setMessages(msgs);
    sessionId.current = session.sessionId || sessionId.current;
    setPlan(null); setViewing(null); setInput(''); setStarted(true);
  };
  const loadPlan = async (id) => { try { const r = await api.get(`/cms/ai/logs/${id}`); if (r.data.session?.plan) setPlan(r.data.session.plan); else toast.error('Ta rozmowa nie zawiera planu'); } catch { toast.error('Błąd'); } };
  const [existingSlugs, setExistingSlugs] = useState(new Set());
  useEffect(() => { api.get('/cms/pages').then(r => setExistingSlugs(new Set((r.data.pages || []).map(p => p.slug)))).catch(() => {}); }, []);

  // Proactive content suggestions
  const [suggestions, setSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [genIdx, setGenIdx] = useState(null);
  const loadSuggestions = async () => {
    setSuggestLoading(true); setSuggestions(null);
    try { const r = await api.post('/cms/ai/assist', { kind: 'suggest_next' }); setSuggestions(r.data.suggestions || []); }
    catch (e) { toast.error(e.response?.data?.error || 'Błąd sugestii'); }
    finally { setSuggestLoading(false); }
  };
  const generateBlogDraft = async (s) => {
    setGenIdx(s.title);
    try {
      const r = await api.post('/cms/ai/assist', { kind: 'blog_post', payload: { topic: s.title } });
      const p = r.data.post;
      if (!p) throw new Error('Brak treści');
      await api.post('/cms/blog/posts', { title: p.title, slug: p.slug, excerpt: p.excerpt, meta_description: p.meta_description, is_published: false, content_blocks: [{ block_type: 'text', content: { html: p.html } }] });
      toast.success('Utworzono szkic wpisu: ' + (p.title || ''));
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd generowania'); }
    finally { setGenIdx(null); }
  };

  // Scheduled (stored) suggestions + schedule config
  const [scheduled, setScheduled] = useState([]);
  const [schedule, setSchedule] = useState({ enabled: false, freq: 'weekly', last_run: null });
  const [schedBusy, setSchedBusy] = useState(false);
  const loadScheduled = useCallback(async () => { try { const r = await api.get('/cms/ai/suggestions'); setScheduled(r.data.suggestions || []); } catch (e) {} }, []);
  const loadSchedule = useCallback(async () => { try { const r = await api.get('/cms/site'); const s = r.data.site || {}; setSchedule({ enabled: !!s.ai_suggest_enabled, freq: s.ai_suggest_freq || 'weekly', last_run: s.ai_suggest_last_run || null }); } catch (e) {} }, []);
  useEffect(() => { loadScheduled(); loadSchedule(); }, [loadScheduled, loadSchedule]);
  const saveSchedule = async (patch) => { const next = { ...schedule, ...patch }; setSchedule(next); try { await api.put('/cms/ai/schedule', { enabled: next.enabled, freq: next.freq }); } catch (e) { toast.error('Błąd zapisu harmonogramu'); } };
  const runNow = async () => { setSchedBusy(true); try { const r = await api.post('/cms/ai/suggestions/run'); setScheduled(r.data.suggestions || []); toast.success(`Dodano ${r.data.added || 0} sugestii`); loadSchedule(); } catch (e) { toast.error(e.response?.data?.error || 'Błąd'); } finally { setSchedBusy(false); } };
  const dismissSuggestion = async (id) => { try { await api.put(`/cms/ai/suggestions/${id}`, { status: 'dismissed' }); loadScheduled(); } catch (e) {} };
  const draftFromStored = async (s) => { await generateBlogDraft(s); try { await api.put(`/cms/ai/suggestions/${s.id}`, { status: 'used' }); } catch (e) {} loadScheduled(); };

  const callInterview = useCallback(async (history, finalize = false) => {
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const r = await api.post('/cms/ai/interview', { messages: history, finalize, sessionId: sessionId.current }, { signal: controller.signal });
      if (r.data.usage) setUsage(r.data.usage);
      if (r.data.done) setPlan(r.data.plan);
      else setMessages(m => [...m, { role: 'assistant', content: r.data.reply }]);
    } catch (e) {
      if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.message === 'canceled') { /* zatrzymane przez użytkownika */ }
      else toast.error(e.response?.data?.error || 'Błąd asystenta AI');
    }
    finally { setLoading(false); abortRef.current = null; }
  }, [toast]);
  const stop = () => { abortRef.current?.abort(); };

  const start = async () => { setStarted(true); await callInterview([]); };
  const send = async () => {
    const text = input.trim(); if (!text || loading) return;
    const next = [...messages, { role: 'user', content: text }];
    setMessages(next); setInput('');
    await callInterview(next);
  };
  const finalize = async () => { if (!loading) await callInterview(messages, true); };
  const apply = async () => {
    setApplying(true);
    try {
      const r = await api.post('/cms/ai/apply', { plan: { ...plan, description: planDesc }, sessionId: sessionId.current });
      const d = r.data;
      toast.success(`Strony: +${d.created?.length || 0} / akt. ${d.updated?.length || 0}${d.galleries?.length ? `, galerie +${d.galleries.length}` : ''}${d.posts?.length ? `, wpisy +${d.posts.length}` : ''}`);
      onApplied?.();
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd zapisu planu'); }
    finally { setApplying(false); }
  };

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  // ---- Plan review ----
  if (plan) {
    const s = plan.site || {};
    const pages = Array.isArray(plan.pages) ? plan.pages : [];
    return (
      <div className="max-w-3xl space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><Sparkles size={18} className="text-purple-600" /> Proponowany plan strony</h2>
          <div className="flex gap-2">
            <button onClick={() => setPlan(null)} className="px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">← Wróć do rozmowy</button>
            <button onClick={apply} disabled={applying} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm flex items-center gap-1">
              {applying ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />} Zastosuj plan
            </button>
          </div>
        </div>
        <p className="text-xs text-amber-600">Nowe podstrony powstaną z treścią i blokami; istniejące zostaną zaktualizowane w zakresie SEO (tytuł, meta, H1, OG) — bloki treści pozostają nietknięte. Menu i ustawienia firmy zostaną uzupełnione. Wszystko dopracujesz potem w edytorze.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-2">Opis działań <span className="font-normal text-xs text-gray-400">(edytowalny)</span></h3>
          <textarea value={planDesc} onChange={e => setPlanDesc(e.target.value)} rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="Szczegółowy opis proponowanych działań…" />
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-2">Ustawienia firmy</h3>
          <div className="text-sm text-gray-600 space-y-1">
            <div><b>{s.site_name || '—'}</b> {s.business_city ? `· ${s.business_city}` : ''}</div>
            {s.site_description && <div className="text-gray-500">{s.site_description}</div>}
            <div className="text-xs text-gray-400">{[s.business_phone, s.business_email, s.business_nip && ('NIP ' + s.business_nip)].filter(Boolean).join(' · ')}</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Podstrony ({pages.length})</h3>
          <div className="space-y-2">
            {pages.map((p, i) => {
              const exists = existingSlugs.has((p.slug || '').toLowerCase()) || existingSlugs.has(p.slug);
              return (
                <div key={i} className="flex items-center gap-2 text-sm border-b border-gray-50 pb-2 last:border-0">
                  {p.is_homepage ? <Home size={14} className="text-amber-500" /> : <FileText size={14} className="text-gray-400" />}
                  <span className="font-medium text-gray-800">{p.title}</span>
                  <span className="text-xs text-gray-400 font-mono">/{p.slug}</span>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{p.page_type || 'content'}</span>
                  {exists
                    ? <span className="text-[10px] text-amber-600">istnieje — aktualizacja SEO</span>
                    : <span className="text-[10px] text-green-600">nowa</span>}
                  <span className="ml-auto text-xs text-gray-400">{(p.blocks || []).length} bloków</span>
                </div>
              );
            })}
            {pages.length === 0 && <div className="text-xs text-amber-600">Plan nie zawiera podstron — wróć do rozmowy („Kontynuuj rozmowę") i poproś o konkretne nowe podstrony lub aktualizacje istniejących.</div>}
          </div>
        </div>

        {plan.menu && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Menu</h3>
            <div>Główne ({(plan.menu.main || []).length}): {(plan.menu.main || []).map(m => m.label).join(', ') || '—'}</div>
            <div className="text-gray-500">Stopka ({(plan.menu.footer || []).length}): {(plan.menu.footer || []).map(m => m.label).join(', ') || '—'}</div>
          </div>
        )}

        {Array.isArray(plan.gallery_categories) && plan.gallery_categories.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Kategorie galerii ({plan.gallery_categories.length})</h3>
            <div>{plan.gallery_categories.map(c => c.name).join(', ')}</div>
            <p className="text-xs text-gray-400 mt-1">Kategorie zostaną utworzone — zdjęcia dodasz w zakładce Galeria.</p>
          </div>
        )}

        {Array.isArray(plan.blog_posts) && plan.blog_posts.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
            <h3 className="text-sm font-bold text-gray-700 mb-2">Szkice wpisów ({plan.blog_posts.length})</h3>
            <div>{plan.blog_posts.map(b => b.title).join(', ')}</div>
            <p className="text-xs text-gray-400 mt-1">Powstaną jako nieopublikowane wpisy w zakładce Blog.</p>
          </div>
        )}
      </div>
    );
  }

  // ---- Two-panel: interview (left) + archive (right) ----
  return (
    <div className="flex gap-5 items-start">
      {/* Left: interview */}
      {!started ? (
        <div className="flex-1 min-w-0 text-center py-12 border border-dashed border-gray-200 rounded-xl">
          <Sparkles size={40} className="mx-auto mb-4 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Kreator strony z AI</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">Asystent przeprowadzi krótki wywiad o Twojej firmie, a następnie zaproponuje kompletną strukturę strony (podstrony, treści, menu, ustawienia) zoptymalizowaną pod SEO. Wszystko zatwierdzasz przed zapisem.</p>
          <button onClick={start} className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium inline-flex items-center gap-2">
            <Sparkles size={16} /> Rozpocznij nowy wywiad
          </button>
        </div>
      ) : (
        <div className="flex-1 min-w-0 flex flex-col" style={{ height: 'calc(100vh - 320px)', minHeight: 360 }}>
          <div ref={scrollRef} className="flex-1 overflow-auto space-y-3 p-1">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}>{m.content}</div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="px-4 py-2.5 rounded-2xl bg-gray-100 text-gray-400 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Asystent pisze…</div></div>}
          </div>
          <div className="mt-3 border-t border-gray-100 pt-3">
            <div className="flex gap-2">
              <textarea value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Napisz odpowiedź…" rows={2}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none" disabled={loading} />
              {loading
                ? <button type="button" onClick={stop} className="px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center gap-1" title="Zatrzymaj"><XCircle size={16} /> Stop</button>
                : <button onClick={send} disabled={!input.trim()} className="px-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg flex items-center"><Send size={16} /></button>}
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-400">Enter wysyła, Shift+Enter = nowa linia</span>
              <button onClick={finalize} disabled={loading || messages.length < 2} className="text-sm text-purple-600 hover:underline disabled:opacity-40 disabled:no-underline flex items-center gap-1">
                <Sparkles size={14} /> Wygeneruj plan strony
              </button>
            </div>
            {usage && (
              <div className="text-[11px] text-gray-400 mt-1">
                Tokeny (okno limitu): pozostało {usage.tokensRemaining ?? '—'}{usage.tokensLimit ? ` / ${usage.tokensLimit}` : ''} · ostatnie zapytanie: ↓{usage.input ?? '—'} ↑{usage.output ?? '—'}{usage.requestsRemaining != null ? ` · zapytań: ${usage.requestsRemaining}` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Right: suggestions + plans + conversations */}
      <div className="w-72 flex-shrink-0 space-y-4">
        {/* Schedule */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input type="checkbox" checked={schedule.enabled} onChange={e => saveSchedule({ enabled: e.target.checked })} /> Auto‑sugestie (harmonogram)
          </label>
          <div className="flex items-center gap-2 mt-2">
            <select value={schedule.freq} onChange={e => saveSchedule({ freq: e.target.value })} className="text-xs border border-gray-300 rounded px-2 py-1">
              <option value="daily">codziennie</option><option value="weekly">co tydzień</option><option value="monthly">co miesiąc</option>
            </select>
            <button onClick={runNow} disabled={schedBusy} className="text-xs text-purple-600 hover:underline disabled:opacity-50 flex items-center gap-1">
              {schedBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Uruchom teraz
            </button>
          </div>
          {schedule.last_run && <div className="text-[11px] text-gray-400 mt-1">Ostatnio: {new Date(schedule.last_run).toLocaleString('pl-PL')}</div>}
        </div>

        {/* Stored (scheduled) suggestions */}
        {scheduled.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2">Propozycje ({scheduled.length})</h3>
            <div className="space-y-2 max-h-60 overflow-auto">
              {scheduled.map(s => (
                <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-2.5">
                  <div className="flex items-center gap-1 mb-0.5"><span className="text-[10px] bg-gray-100 text-gray-600 px-1 rounded">{s.type}</span>{s.segment && <span className="text-[10px] text-gray-400 truncate">{s.segment}</span>}</div>
                  <div className="text-xs font-medium text-gray-800">{s.title}</div>
                  <div className="text-[11px] text-gray-500">{s.reason}</div>
                  <div className="flex justify-between items-center mt-1">
                    {s.type === 'blog'
                      ? <button onClick={() => draftFromStored(s)} disabled={genIdx === s.title} className="text-[11px] text-purple-600 hover:underline disabled:opacity-50">Wygeneruj szkic</button>
                      : <span />}
                    <button onClick={() => dismissSuggestion(s.id)} className="text-[11px] text-gray-400 hover:text-red-500">Odrzuć</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={loadSuggestions} disabled={suggestLoading}
          className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2">
          {suggestLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Sugestie ad hoc
        </button>

        {/* Plans */}
        {archive.some(s => s.hasPlan) && (
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-2">Plany ({archive.filter(s => s.hasPlan).length})</h3>
            <div className="space-y-2 max-h-56 overflow-auto">
              {archive.filter(s => s.hasPlan).map(s => (
                <div key={s.sessionId} className="bg-white border border-gray-200 rounded-lg p-2.5 group">
                  <button onClick={() => openSession(s.sessionId)} className="w-full text-left">
                    <div className="text-xs font-medium text-gray-800 line-clamp-2">{s.preview || '(plan)'}</div>
                    <div className="text-[11px] text-gray-400 mt-1">{s.updatedAt ? new Date(s.updatedAt).toLocaleString('pl-PL') : ''}{s.userName ? ` · ${s.userName}` : ''}{s.tokens ? ` · ${s.tokens} tok.` : ''}{s.appliedAt && <span className="ml-1 text-blue-600">· zast.</span>}</div>
                  </button>
                  <div className="flex justify-between items-center mt-1">
                    <button onClick={() => loadPlan(s.sessionId)} className="text-[11px] text-purple-600 hover:underline">Wczytaj plan</button>
                    <button onClick={() => setDelSession(s.sessionId)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded"><Trash2 size={12} className="text-red-400" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conversations */}
        <div>
          <h3 className="text-sm font-bold text-gray-700 mb-2">Rozmowy{archive.length ? ` (${archive.length})` : ''}</h3>
          {archive.length === 0 && <p className="text-xs text-gray-400">Brak zapisanych rozmów.</p>}
          <div className="space-y-2 max-h-72 overflow-auto">
            {archive.map(s => (
              <div key={s.sessionId} className="bg-white border border-gray-200 rounded-lg p-2.5 group">
                <button onClick={() => openSession(s.sessionId)} className="w-full text-left">
                  <div className="text-xs text-gray-800 line-clamp-2">{s.preview || '(rozmowa)'}</div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    {s.updatedAt ? new Date(s.updatedAt).toLocaleString('pl-PL') : ''}{s.userName ? ` · ${s.userName}` : ''} · {s.messages} wiad.{s.tokens ? ` · ${s.tokens} tok.` : ''}
                    {s.hasPlan && <span className="ml-1 text-green-600">· plan</span>}
                  </div>
                </button>
                <div className="flex justify-end mt-1 opacity-0 group-hover:opacity-100">
                  <button onClick={() => setDelSession(s.sessionId)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={13} className="text-red-400" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Suggestions modal */}
      <Modal isOpen={suggestions !== null} onClose={() => setSuggestions(null)} title="Sugestie: co dodać, by strona żyła" maxWidth="max-w-xl">
        {suggestions !== null && (
          <div className="space-y-3">
            {suggestions.length === 0 && <p className="text-sm text-gray-500">Brak sugestii.</p>}
            {suggestions.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s.type}</span>
                  {s.segment && <span className="text-xs text-gray-400">{s.segment}</span>}
                </div>
                <div className="text-sm font-medium text-gray-800">{s.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.reason}</div>
                {s.type === 'blog' && (
                  <button onClick={() => generateBlogDraft(s)} disabled={genIdx === s.title}
                    className="mt-2 text-xs text-purple-600 hover:underline flex items-center gap-1 disabled:opacity-50">
                    {genIdx === s.title ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Wygeneruj szkic wpisu
                  </button>
                )}
              </div>
            ))}
            <p className="text-xs text-gray-400">Szkice wpisów trafiają do zakładki Blog jako nieopublikowane — możesz je dopracować i opublikować.</p>
          </div>
        )}
      </Modal>

      {/* Conversation viewer */}
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)} title="Zarchiwizowana rozmowa" maxWidth="max-w-2xl">
        {viewing && (
          <div>
            <div className="text-xs text-gray-400 mb-3">{viewing.updatedAt ? new Date(viewing.updatedAt).toLocaleString('pl-PL') : ''}{viewing.userName ? ` · ${viewing.userName}` : ''}{viewing.tokens ? ` · ${viewing.tokens} tokenów` : ''}</div>
            <div className="space-y-3 max-h-[50vh] overflow-auto mb-4">
              {(viewing.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'}`}>{typeof m.content === 'string' ? m.content : JSON.stringify(m.content)}</div>
                </div>
              ))}
            </div>
            {viewing.plan && (
              <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
                <div className="text-xs font-bold text-gray-700 mb-2">Plan: {(viewing.plan.pages || []).length} podstron · menu {(viewing.plan.menu?.main || []).length}+{(viewing.plan.menu?.footer || []).length}{viewing.appliedAt ? ` · zastosowano ${new Date(viewing.appliedAt).toLocaleString('pl-PL')}` : ''}</div>
                {(viewing.plan.description || viewing.plan.notes) && <div className="text-xs text-gray-600 whitespace-pre-wrap mb-2 max-h-32 overflow-auto border-b border-gray-200 pb-2">{viewing.plan.description || viewing.plan.notes}</div>}
                <div className="space-y-1 max-h-48 overflow-auto">
                  {(viewing.plan.pages || []).map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {p.is_homepage ? <Home size={13} className="text-amber-500 flex-shrink-0" /> : <FileText size={13} className="text-gray-400 flex-shrink-0" />}
                      <span className="text-gray-800 truncate">{p.title}</span>
                      <span className="text-xs text-gray-400 font-mono">/{p.slug}</span>
                      <span className="text-[10px] bg-gray-100 text-gray-500 px-1 rounded">{p.page_type || 'content'}</span>
                      <span className="ml-auto text-[11px] text-gray-400">{(p.blocks || []).length} bl.</span>
                    </div>
                  ))}
                </div>
                {viewing.plan.menu && <div className="text-[11px] text-gray-500 mt-2">Menu: {(viewing.plan.menu.main || []).map(m => m.label).join(', ') || '—'}</div>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDelSession(viewing.sessionId)} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg mr-auto">Usuń</button>
              <button onClick={() => resumeSession(viewing)} className="px-4 py-2 text-sm border border-purple-300 text-purple-700 hover:bg-purple-50 rounded-lg flex items-center gap-1"><Sparkles size={14} /> Kontynuuj rozmowę</button>
              {viewing.plan && <button onClick={() => usePlanFromSession(viewing)} className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">Wczytaj plan</button>}
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg">Zamknij</button>
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!delSession} type="danger" title="Usuń rozmowę" message="Usunąć zarchiwizowaną rozmowę z plików?" onConfirm={removeSession} onCancel={() => setDelSession(null)} />
    </div>
  );
}

// ==================== BLOG PANEL ====================

function BlogPanel({ toast, canCreate, canEdit, canDelete }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get('/cms/blog/posts'); setPosts(r.data.posts); }
    catch { toast.error('Błąd ładowania'); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = async (id) => {
    try { const r = await api.get(`/cms/blog/posts/${id}`); setEditing(r.data.post); setShowForm(true); }
    catch { toast.error('Błąd'); }
  };
  const save = async (data) => {
    try {
      if (editing) await api.put(`/cms/blog/posts/${editing.id}`, data);
      else await api.post('/cms/blog/posts', data);
      setShowForm(false); setEditing(null); load();
      toast.success('Zapisano wpis');
    } catch (e) { toast.error(e.response?.data?.error || 'Błąd zapisu'); }
  };
  const remove = async () => {
    try { await api.delete(`/cms/blog/posts/${del.id}`); setDel(null); load(); }
    catch { toast.error('Błąd'); }
  };

  if (loading) return <div className="p-8 text-gray-400">Ładowanie…</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Wpisy blogowe</h2>
          <p className="text-xs text-gray-500">Posty renderowane są jako statyczne strony pod <span className="font-mono">/blog/&lt;slug&gt;</span> z danymi strukturalnymi Article. Utwórz stronę typu „Lista bloga", by mieć indeks.</p>
        </div>
        {canCreate && <button onClick={openNew} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm flex-shrink-0"><Plus size={14} /> Nowy wpis</button>}
      </div>

      {posts.length === 0 && <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg"><FileText size={36} className="mx-auto mb-2 opacity-50" /><p>Brak wpisów — dodaj pierwszy</p></div>}

      <div className="space-y-2">
        {posts.map(p => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3 group">
            {p.featured_image
              ? <img src={mediaUrl(p.featured_image)} alt="" className="w-14 h-10 object-cover rounded flex-shrink-0" />
              : <div className="w-14 h-10 bg-gray-100 rounded flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-800 truncate flex items-center gap-2">{p.title}<span className="text-xs text-gray-400 font-mono">/{p.slug}</span></div>
              <div className="text-xs text-gray-500">{p.published_at ? new Date(p.published_at).toLocaleDateString('pl-PL') : 'szkic'}{p.author_name ? ` · ${p.author_name}` : ''}</div>
            </div>
            {p.is_published ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-gray-300" />}
            <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              {canEdit && <button onClick={() => openEdit(p.id)} className="p-1 hover:bg-gray-100 rounded"><Pencil size={14} className="text-gray-500" /></button>}
              {canDelete && <button onClick={() => setDel(p)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? 'Edytuj wpis' : 'Nowy wpis'} maxWidth="max-w-2xl">
        <PostForm initial={editing} toast={toast} onSave={save} onCancel={() => { setShowForm(false); setEditing(null); }} />
      </Modal>

      <ConfirmDialog isOpen={!!del} type="danger" title="Usuń wpis" message={`Usunąć wpis „${del?.title}"?`} onConfirm={remove} onCancel={() => setDel(null)} />
    </div>
  );
}

function PostForm({ initial, toast, onSave, onCancel }) {
  const bodyFrom = (p) => {
    const blocks = Array.isArray(p?.content_blocks) ? p.content_blocks : [];
    const textBlock = blocks.find(b => b.block_type === 'text');
    return textBlock?.content?.html || '';
  };
  const [form, setForm] = useState({
    title: initial?.title || '', slug: initial?.slug || '', excerpt: initial?.excerpt || '',
    featured_image: initial?.featured_image || '', tags: (initial?.tags || []).join(', '),
    meta_description: initial?.meta_description || '',
    is_published: initial?.is_published || false,
    published_at: initial?.published_at ? new Date(initial.published_at).toISOString().slice(0, 10) : '',
    body: bodyFrom(initial),
  });
  const [uploading, setUploading] = useState(false);
  const ic = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm";
  const genSlug = (t) => t.toLowerCase().replace(/[ąàáâ]/g,'a').replace(/[ćč]/g,'c').replace(/[ęèéê]/g,'e').replace(/[łĺ]/g,'l').replace(/[ńñ]/g,'n').replace(/[óòôõ]/g,'o').replace(/[śš]/g,'s').replace(/[żźž]/g,'z').replace(/[ůúùû]/g,'u').replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'');

  const [aiBusy, setAiBusy] = useState(false);
  const aiWrite = async () => {
    setAiBusy(true);
    try {
      const r = await api.post('/cms/ai/assist', { kind: 'blog_post', payload: { topic: form.title || 'wpis blogowy pasujący do firmy i dotychczasowych treści' } });
      const p = r.data.post;
      if (p) setForm(f => ({ ...f, title: f.title || p.title || '', slug: f.slug || genSlug(p.title || f.title || ''), excerpt: p.excerpt || f.excerpt, meta_description: p.meta_description || f.meta_description, body: p.html || f.body }));
      else toast?.error('AI nie zwróciło treści');
    } catch (e) { toast?.error(e.response?.data?.error || 'Błąd AI'); }
    finally { setAiBusy(false); }
  };

  const uploadFeatured = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/cms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setForm(f => ({ ...f, featured_image: r.data.filename })); }
    catch (e) { toast?.error('Błąd wgrywania'); }
    finally { setUploading(false); }
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      title: form.title,
      slug: form.slug || undefined,
      excerpt: form.excerpt,
      featured_image: form.featured_image,
      tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
      meta_description: form.meta_description,
      is_published: form.is_published,
      published_at: form.published_at || null,
      content_blocks: [{ block_type: 'text', content: { html: form.body } }],
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="flex justify-end">
        <button type="button" onClick={aiWrite} disabled={aiBusy} className="text-xs text-purple-600 hover:underline disabled:opacity-50 flex items-center gap-1">
          {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Napisz wpis z AI {form.title ? '(na podstawie tytułu)' : '(na podstawie kontekstu firmy)'}
        </button>
      </div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Tytuł *</label>
        <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value, slug: initial ? form.slug : genSlug(e.target.value) })} className={ic} required autoFocus /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Slug (URL) *</label>
        <input type="text" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className={ic + " font-mono"} required /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Zajawka (excerpt)</label>
        <textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} className={ic} rows={2} /></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Obraz wyróżniający</label>
        <div className="flex items-center gap-3">
          {form.featured_image ? <img src={mediaUrl(form.featured_image)} alt="" className="h-14 w-24 object-cover rounded border" /> : <div className="h-14 w-24 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-300 text-xs">brak</div>}
          <input type="file" accept="image/*" onChange={e => uploadFeatured(e.target.files[0])} className="text-xs" />
          {uploading && <span className="text-xs text-gray-400">Wgrywanie…</span>}
          {form.featured_image && <button type="button" onClick={() => setForm({ ...form, featured_image: '' })} className="text-xs text-red-500 hover:underline">Usuń</button>}
        </div></div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Treść (HTML)</label>
        <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className={ic + " font-mono"} rows={8} placeholder="<p>Treść wpisu…</p>" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Tagi (po przecinku)</label>
          <input type="text" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} className={ic} /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">Data publikacji</label>
          <input type="date" value={form.published_at} onChange={e => setForm({ ...form, published_at: e.target.value })} className={ic} /></div>
      </div>
      <div><label className="block text-xs font-medium text-gray-600 mb-1">Meta description (SEO)</label>
        <textarea value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} className={ic} rows={2} /></div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} /> Opublikowany</label>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Anuluj</button>
        <button type="submit" className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg">{initial ? 'Zapisz' : 'Utwórz'}</button>
      </div>
    </form>
  );
}

// ==================== MENU EDITOR ====================

const MENU_LOCATIONS = [
  { key: 'main', label: 'Menu główne (nagłówek)', hint: 'Pozycje w nagłówku strony. Pozycja z podpozycjami pojawi się jako rozwijane menu.' },
  { key: 'footer', label: 'Menu stopki', hint: 'Pozycje w stopce. Pozycja nadrzędna staje się nagłówkiem kolumny, a podpozycje — linkami pod nią.' },
];

function MenuEditor({ pages, toast, canEdit }) {
  const [data, setData] = useState({ main: [], footer: [] });
  const [loc, setLoc] = useState('main');
  const [dirty, setDirty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/cms/menus');
      const menus = r.data.menus || [];
      const get = (l) => { const m = menus.find(x => x.location === l); return Array.isArray(m?.items) ? m.items : []; };
      setData({ main: get('main'), footer: get('footer') });
    } catch { toast.error('Błąd ładowania menu'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const items = data[loc] || [];
  const update = (next) => { setData(d => ({ ...d, [loc]: next })); setDirty(s => ({ ...s, [loc]: true })); };

  // top-level operations
  const childrenOf = (it) => Array.isArray(it.children) ? it.children : [];
  const addBlank = () => update([...items, { label: '', url: '', children: [] }]);
  const addPage = (page) => { if (page) update([...items, { label: page.title, url: '/' + page.slug, children: [] }]); };
  const removeItem = (i) => update(items.filter((_, idx) => idx !== i));
  const editField = (i, field, value) => update(items.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[i], next[j]] = [next[j], next[i]]; update(next);
  };

  // sub-item (children) operations
  const setChildren = (i, ch) => update(items.map((it, idx) => idx === i ? { ...it, children: ch } : it));
  const addChild = (i) => setChildren(i, [...childrenOf(items[i]), { label: '', url: '' }]);
  const addChildPage = (i, page) => { if (page) setChildren(i, [...childrenOf(items[i]), { label: page.title, url: '/' + page.slug }]); };
  const editChild = (i, ci, field, value) => setChildren(i, childrenOf(items[i]).map((c, idx) => idx === ci ? { ...c, [field]: value } : c));
  const removeChild = (i, ci) => setChildren(i, childrenOf(items[i]).filter((_, idx) => idx !== ci));
  const moveChild = (i, ci, dir) => {
    const ch = childrenOf(items[i]); const j = ci + dir; if (j < 0 || j >= ch.length) return;
    const next = [...ch]; [next[ci], next[j]] = [next[j], next[ci]]; setChildren(i, next);
  };

  const save = async () => {
    const clean = items
      .filter(it => (it.label || '').trim() || (it.url || '').trim() || childrenOf(it).length)
      .map(it => ({
        label: it.label || '', url: it.url || '',
        children: childrenOf(it)
          .filter(c => (c.label || '').trim() || (c.url || '').trim())
          .map(c => ({ label: c.label || '', url: c.url || '' })),
      }));
    setSaving(true);
    try {
      await api.put(`/cms/menus/${loc}`, { items: clean });
      setData(d => ({ ...d, [loc]: clean }));
      setDirty(s => ({ ...s, [loc]: false }));
      toast.success('Menu zapisane');
    } catch { toast.error('Błąd zapisu menu'); }
    finally { setSaving(false); }
  };

  const publishedPages = (pages || []).filter(p => p.is_published);
  const current = MENU_LOCATIONS.find(l => l.key === loc);

  if (loading) return <div className="p-8 text-gray-400">Ładowanie menu...</div>;

  const rowInput = "min-w-0 px-3 py-1.5 border border-gray-300 rounded-lg text-sm";

  return (
    <div className="max-w-3xl">
      {/* Location switcher */}
      <div className="flex items-center gap-2 mb-4">
        {MENU_LOCATIONS.map(l => (
          <button key={l.key} onClick={() => setLoc(l.key)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${loc === l.key ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {l.label}{dirty[l.key] ? ' •' : ''}
          </button>
        ))}
      </div>

      <div className="flex items-start justify-between mb-4 gap-4">
        <p className="text-xs text-gray-500">{current?.hint}{' '}URL wewnętrzny to <span className="font-mono">/slug</span> podstrony; można też wkleić pełny link zewnętrzny (https://…).</p>
        {canEdit && (
          <button onClick={save} disabled={saving || !dirty[loc]}
            className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg flex-shrink-0">
            {saving ? 'Zapisywanie...' : 'Zapisz menu'}
          </button>
        )}
      </div>

      {/* URL suggestions from pages */}
      <datalist id="cms-page-slugs">
        {publishedPages.map(p => <option key={p.id} value={'/' + p.slug}>{p.title}</option>)}
      </datalist>

      {items.length === 0 && (
        <div className="text-center py-12 text-gray-400 border border-dashed border-gray-200 rounded-lg mb-3">
          <Menu size={40} className="mx-auto mb-3 opacity-50" />
          <p>To menu jest puste — dodaj pierwszą pozycję</p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="bg-white border border-gray-200 rounded-lg p-2">
            {/* Top-level row */}
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="text-gray-300 flex-shrink-0" />
              <input type="text" value={item.label || ''} disabled={!canEdit}
                onChange={e => editField(i, 'label', e.target.value)}
                placeholder="Etykieta" className={`flex-1 ${rowInput}`} />
              <input type="text" value={item.url || ''} disabled={!canEdit} list="cms-page-slugs"
                onChange={e => editField(i, 'url', e.target.value)}
                placeholder="/slug lub https://..." className={`flex-1 ${rowInput} font-mono`} />
              {canEdit && (
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                  <button onClick={() => removeItem(i)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                </div>
              )}
            </div>

            {/* Sub-items */}
            {(childrenOf(item).length > 0 || canEdit) && (
              <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-100 space-y-2">
                {childrenOf(item).map((child, ci) => (
                  <div key={ci} className="flex items-center gap-2">
                    <ChevronDown size={12} className="text-gray-300 flex-shrink-0 -rotate-90" />
                    <input type="text" value={child.label || ''} disabled={!canEdit}
                      onChange={e => editChild(i, ci, 'label', e.target.value)}
                      placeholder="Etykieta podpozycji" className={`flex-1 ${rowInput}`} />
                    <input type="text" value={child.url || ''} disabled={!canEdit} list="cms-page-slugs"
                      onChange={e => editChild(i, ci, 'url', e.target.value)}
                      placeholder="/slug lub https://..." className={`flex-1 ${rowInput} font-mono`} />
                    {canEdit && (
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button onClick={() => moveChild(i, ci, -1)} disabled={ci === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button onClick={() => moveChild(i, ci, 1)} disabled={ci === childrenOf(item).length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                        <button onClick={() => removeChild(i, ci)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                      </div>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => addChild(i)} className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                      <Plus size={12} /> Dodaj podpozycję
                    </button>
                    {publishedPages.length > 0 && (
                      <select value="" onChange={e => { const p = publishedPages.find(pp => String(pp.id) === e.target.value); addChildPage(i, p); e.target.value = ''; }}
                        className="px-2 py-1 border border-gray-200 text-gray-500 rounded text-xs bg-white">
                        <option value="">+ ze strony…</option>
                        {publishedPages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <button onClick={addBlank} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50 rounded-lg text-sm">
            <Plus size={14} /> Dodaj pozycję
          </button>
          {publishedPages.length > 0 && (
            <select value="" onChange={e => { const p = publishedPages.find(pp => String(pp.id) === e.target.value); addPage(p); e.target.value = ''; }}
              className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm bg-white">
              <option value="">+ Dodaj z istniejącej strony…</option>
              {publishedPages.map(p => <option key={p.id} value={p.id}>{p.title} (/{p.slug})</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}
